import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    Collapse,
    IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, SportsBaseball } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import {
    BaseballRecruitProfile,
    BattingStats,
    PitchingStats,
    FieldingStats,
    SeasonStats,
} from 'api/BaseballRecruits';

type VisibleLevelsSet = Set<string>;

interface BaseballCardProps {
    profile: BaseballRecruitProfile;
    onExpand?: () => void;
    visibleLevels?: VisibleLevelsSet;
}

const StatBox: React.FC<{
    label: string;
    value: string | undefined;
}> = ({ label, value }) => {
    return (
        <Box sx={{ textAlign: 'center', px: 1 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    fontFamily: '"Roboto Condensed", "Arial Narrow", sans-serif',
                }}
            >
                {value || '-'}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.65rem',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

const BATTING_COLUMNS: { key: string & keyof BattingStats; label: string }[] = [
    { key: 'GP', label: 'GP' },
    { key: 'AVG', label: 'AVG' },
    { key: 'AB', label: 'AB' },
    { key: 'R', label: 'R' },
    { key: 'H', label: 'H' },
    { key: 'RBI', label: 'RBI' },
    { key: '2B', label: '2B' },
    { key: '3B', label: '3B' },
    { key: 'HR', label: 'HR' },
    { key: 'BB', label: 'BB' },
    { key: 'K', label: 'K' },
    { key: 'SB', label: 'SB' },
    { key: 'OBP', label: 'OBP' },
    { key: 'SLG', label: 'SLG' },
    { key: 'OPS', label: 'OPS' },
];

const PITCHING_COLUMNS: { key: string & keyof PitchingStats; label: string }[] = [
    { key: 'ERA', label: 'ERA' },
    { key: 'W', label: 'W' },
    { key: 'L', label: 'L' },
    { key: 'APP', label: 'APP' },
    { key: 'GS', label: 'GS' },
    { key: 'SV', label: 'SV' },
    { key: 'IP', label: 'IP' },
    { key: 'H', label: 'H' },
    { key: 'ER', label: 'ER' },
    { key: 'BB', label: 'BB' },
    { key: 'K', label: 'K' },
    { key: 'WHIP', label: 'WHIP' },
];

const FIELDING_COLUMNS: { key: string & keyof FieldingStats; label: string }[] = [
    { key: 'GP', label: 'GP' },
    { key: 'FP', label: 'FP' },
    { key: 'TC', label: 'TC' },
    { key: 'PO', label: 'PO' },
    { key: 'A', label: 'A' },
    { key: 'E', label: 'E' },
    { key: 'DP', label: 'DP' },
];

const SCHOOL_ABBREVS: Record<string, string> = {
    'arizona st.': 'ASU',
    'arizona state': 'ASU',
    'florida st.': 'FSU',
    'florida state': 'FSU',
    'michigan st.': 'MSU',
    'michigan state': 'MSU',
    'ohio st.': 'OSU',
    'ohio state': 'OSU',
    'oklahoma st.': 'Okla. St.',
    'oklahoma state': 'Okla. St.',
    'oregon st.': 'Ore. St.',
    'oregon state': 'Ore. St.',
    'penn st.': 'Penn St.',
    'penn state': 'Penn St.',
    'mississippi st.': 'Miss. St.',
    'mississippi state': 'Miss. St.',
    'texas a&m': 'TAMU',
    ucla: 'UCLA',
    usc: 'USC',
    lsu: 'LSU',
    tcu: 'TCU',
    'uc berkeley': 'Cal',
    'uc santa barbara': 'UCSB',
    'uc irvine': 'UCI',
    'uc davis': 'UC Davis',
    'central florida': 'UCF',
    'southern california': 'USC',
    'north carolina': 'UNC',
    'south carolina': 'S. Carolina',
    'virginia tech': 'Va. Tech',
    'georgia tech': 'Ga. Tech',
    'texas tech': 'Texas Tech',
};

const BASEBALL_POSITIONS = new Set([
    'P',
    'C',
    '1B',
    '2B',
    '3B',
    'SS',
    'LF',
    'CF',
    'RF',
    'OF',
    'DH',
    'IF',
    'UT',
    'UTIL',
    'RHP',
    'LHP',
    'RP',
    'SP',
    'CL',
    'INF',
    'MIF',
    'CIF',
]);

const isBaseballPosition = (pos: string): boolean =>
    BASEBALL_POSITIONS.has(pos.toUpperCase().trim());

const abbreviateSchool = (name: string): string => {
    if (!name) return '';
    const trimmed = name.trim();
    const abbrev = SCHOOL_ABBREVS[trimmed.toLowerCase()];
    if (abbrev) return abbrev;
    if (trimmed.length <= 12) return trimmed;
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) {
        const twoWord = `${parts[0]} ${parts[1]}`;
        if (twoWord.length <= 12) return twoWord;
    }
    return parts[0].length <= 12 ? parts[0] : parts[0].slice(0, 12);
};

const META_CELL_HEADER_SX = {
    fontSize: '0.6rem',
    fontWeight: 700,
    py: 0.25,
    px: 0.5,
    whiteSpace: 'nowrap' as const,
};

const STAT_CELL_HEADER_SX = {
    fontSize: '0.6rem',
    fontWeight: 700,
    py: 0.25,
    px: 0.5,
    textAlign: 'center' as const,
};

const META_CELL_SX = {
    fontSize: '0.7rem',
    py: 0.25,
    px: 0.5,
    whiteSpace: 'nowrap' as const,
};

const STAT_CELL_SX = {
    fontSize: '0.7rem',
    py: 0.25,
    px: 0.5,
    textAlign: 'center' as const,
};

const SectionDivider: React.FC<{ label: string; color?: string }> = ({
    label,
    color = '#1565c0',
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            mt: 0.5,
            backgroundColor: alpha(color, 0.08),
            borderTop: `2px solid ${alpha(color, 0.3)}`,
        }}
    >
        <Typography
            variant="caption"
            sx={{
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color,
                fontSize: '0.65rem',
            }}
        >
            {label}
        </Typography>
    </Box>
);

const GRADE_LABELS = ['Fr.', 'So.', 'Jr.', 'Sr.'];
const gradeFromGradYear = (gradYear: string | undefined, seasonYear: string): string => {
    if (!gradYear) return '';
    const diff = parseInt(seasonYear, 10) - parseInt(gradYear, 10);
    if (diff >= 1 && diff <= 4) return GRADE_LABELS[diff - 1];
    if (diff > 4) return 'Sr.';
    return '';
};

const BattingTable: React.FC<{
    seasons: SeasonStats[];
    careerTotals: BattingStats | null | undefined;
    theme: any;
    schoolName?: string;
    playerClassYear?: string;
    gradYear?: string;
    variant?: 'default' | 'summer';
}> = ({
    seasons,
    careerTotals,
    theme,
    schoolName,
    playerClassYear,
    gradYear,
    variant = 'default',
}) => {
    const battingSeasons = seasons.filter((s) => s.batting);
    if (battingSeasons.length === 0 && !careerTotals) return null;

    const isSummer = variant === 'summer';

    return (
        <Box sx={{ px: 1, pt: 0.5 }}>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, px: 0.5, display: 'block', mb: 0.5 }}
            >
                Batting
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={META_CELL_HEADER_SX}>
                                {isSummer ? 'Age' : 'GD'}
                            </TableCell>
                            <TableCell sx={META_CELL_HEADER_SX}>Team</TableCell>
                            {isSummer && <TableCell sx={META_CELL_HEADER_SX}>Year</TableCell>}
                            <TableCell sx={META_CELL_HEADER_SX}>
                                {isSummer ? 'League' : 'Year'}
                            </TableCell>
                            {BATTING_COLUMNS.map((col) => (
                                <TableCell key={col.key} sx={STAT_CELL_HEADER_SX}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {battingSeasons.map((s, i) => {
                            const teamDisplay = abbreviateSchool(
                                s.school_name || schoolName || (s.team_level || '').slice(0, 3),
                            );
                            return (
                                <TableRow key={i}>
                                    <TableCell sx={META_CELL_SX}>
                                        {isSummer
                                            ? s.age || '-'
                                            : gradeFromGradYear(gradYear, s.year) ||
                                              s.grade ||
                                              playerClassYear ||
                                              '-'}
                                    </TableCell>
                                    <TableCell sx={META_CELL_SX}>{teamDisplay}</TableCell>
                                    {isSummer && (
                                        <TableCell sx={META_CELL_SX}>{s.year || '-'}</TableCell>
                                    )}
                                    <TableCell sx={META_CELL_SX}>
                                        {isSummer ? s.league_name || s.league_abbr || '-' : s.year}
                                    </TableCell>
                                    {BATTING_COLUMNS.map((col) => (
                                        <TableCell key={col.key} sx={STAT_CELL_SX}>
                                            {s.batting?.[col.key] || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {careerTotals && (
                            <TableRow
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                }}
                            >
                                <TableCell
                                    colSpan={isSummer ? 4 : 3}
                                    sx={{ ...META_CELL_SX, fontWeight: 800 }}
                                >
                                    Career Total
                                </TableCell>
                                {BATTING_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        sx={{ ...STAT_CELL_SX, fontWeight: 700 }}
                                    >
                                        {careerTotals[col.key] || '-'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const PitchingTable: React.FC<{
    seasons: SeasonStats[];
    careerTotals: PitchingStats | null | undefined;
    theme: any;
    schoolName?: string;
    playerClassYear?: string;
    gradYear?: string;
    variant?: 'default' | 'summer';
}> = ({
    seasons,
    careerTotals,
    theme,
    schoolName,
    playerClassYear,
    gradYear,
    variant = 'default',
}) => {
    const pitchingSeasons = seasons.filter((s) => s.pitching);
    if (pitchingSeasons.length === 0 && !careerTotals) return null;

    const isSummer = variant === 'summer';

    return (
        <Box sx={{ px: 1, pt: 0.5 }}>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, px: 0.5, display: 'block', mb: 0.5 }}
            >
                Pitching
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={META_CELL_HEADER_SX}>
                                {isSummer ? 'Age' : 'GD'}
                            </TableCell>
                            <TableCell sx={META_CELL_HEADER_SX}>Team</TableCell>
                            {isSummer && <TableCell sx={META_CELL_HEADER_SX}>Year</TableCell>}
                            <TableCell sx={META_CELL_HEADER_SX}>
                                {isSummer ? 'League' : 'Year'}
                            </TableCell>
                            {PITCHING_COLUMNS.map((col) => (
                                <TableCell key={col.key} sx={STAT_CELL_HEADER_SX}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pitchingSeasons.map((s, i) => {
                            const teamDisplay = abbreviateSchool(
                                s.school_name || schoolName || (s.team_level || '').slice(0, 3),
                            );
                            return (
                                <TableRow key={i}>
                                    <TableCell sx={META_CELL_SX}>
                                        {isSummer
                                            ? s.age || '-'
                                            : gradeFromGradYear(gradYear, s.year) ||
                                              s.grade ||
                                              playerClassYear ||
                                              '-'}
                                    </TableCell>
                                    <TableCell sx={META_CELL_SX}>{teamDisplay}</TableCell>
                                    {isSummer && (
                                        <TableCell sx={META_CELL_SX}>{s.year || '-'}</TableCell>
                                    )}
                                    <TableCell sx={META_CELL_SX}>
                                        {isSummer ? s.league_name || s.league_abbr || '-' : s.year}
                                    </TableCell>
                                    {PITCHING_COLUMNS.map((col) => (
                                        <TableCell key={col.key} sx={STAT_CELL_SX}>
                                            {s.pitching?.[col.key] || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {careerTotals && (
                            <TableRow
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                }}
                            >
                                <TableCell
                                    colSpan={isSummer ? 4 : 3}
                                    sx={{ ...META_CELL_SX, fontWeight: 800 }}
                                >
                                    Career Total
                                </TableCell>
                                {PITCHING_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        sx={{ ...STAT_CELL_SX, fontWeight: 700 }}
                                    >
                                        {careerTotals[col.key] || '-'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const ALL_LEVELS = new Set(['college', 'summer', 'hs']);

const BaseballCard: React.FC<BaseballCardProps> = ({ profile, visibleLevels }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const levels = visibleLevels ?? ALL_LEVELS;
    const showCollege = levels.has('college');
    const showSummer = levels.has('summer');
    const showHs = levels.has('hs');

    const data = profile.scraped_data;
    const collegeData = profile.college_scraped_data;
    const bio = profile.player_bio || data?.player;

    const collegeBio = collegeData?.player;
    const displayName = profile.individual?.name
        ? profile.individual.name
        : collegeBio?.first_name && collegeBio?.last_name
        ? `${collegeBio.first_name} ${collegeBio.last_name}`
        : bio?.first_name && bio?.last_name
        ? `${bio.first_name} ${bio.last_name}`
        : 'Unknown';
    const displayInitials =
        displayName !== 'Unknown'
            ? displayName
                  .split(' ')
                  .map((w) => w[0])
                  .join('')
            : '?';

    // High school data
    const hsSeasons = data?.seasons_data || [];
    const hsCareerTotals = data?.career_totals || null;
    const hsLatestSeason = hsSeasons[0] || null;
    const hasHsData = hsSeasons.length > 0 || hsCareerTotals;

    // College data
    const collegeSeasons = collegeData?.seasons_data || [];
    const collegeCareerTotals = collegeData?.career_totals || null;
    const collegeLatestSeason = collegeSeasons[0] || null;
    const hasCollegeData = collegeSeasons.length > 0 || collegeCareerTotals;

    // College metadata
    const collegeSchoolName = collegeData?.scrape_metadata?.school_name || '';
    const collegeSchoolAbbr = abbreviateSchool(collegeSchoolName);
    const collegePlayerClassYear = collegeData?.player?.class_year || '';

    // Summer league data
    const summerData = profile.summer_league_data;
    const summerSeasons = summerData?.seasons_data || [];
    const summerCareerTotals = summerData?.career_totals || null;
    const summerLatestSeason = summerSeasons[0] || null;
    const hasSummerData = summerSeasons.length > 0 || summerCareerTotals;

    // Highlight stats per level
    const collegeBatting = collegeCareerTotals?.batting || collegeLatestSeason?.batting || null;
    const collegePitching = collegeCareerTotals?.pitching || collegeLatestSeason?.pitching || null;
    const summerBatting = summerCareerTotals?.batting || summerLatestSeason?.batting || null;
    const summerPitching = summerCareerTotals?.pitching || summerLatestSeason?.pitching || null;
    const hsBatting = hsCareerTotals?.batting || hsLatestSeason?.batting || null;
    const hsPitching = hsCareerTotals?.pitching || hsLatestSeason?.pitching || null;
    const visibleLevelCount = [
        showCollege && hasCollegeData,
        showSummer && hasSummerData,
        showHs && hasHsData,
    ].filter(Boolean).length;
    const hasMultipleLevels = visibleLevelCount > 1;

    const gradient = 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)';
    const photoSrc = profile.photo_url || profile.individual?.profile_photo || '';

    return (
        <Box
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                background: theme.palette.background.paper,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 30px ${alpha(theme.palette.common.black, 0.15)}`,
                },
            }}
        >
            {/* Card Header with gradient */}
            <Box
                sx={{
                    background: gradient,
                    color: '#fff',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    {photoSrc ? (
                        <Avatar
                            src={photoSrc}
                            variant="rounded"
                            imgProps={{ style: { objectPosition: 'top' } }}
                            sx={{
                                width: 52,
                                height: 64,
                                border: '2px solid rgba(255,255,255,0.4)',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: 52,
                                height: 64,
                                border: '2px solid rgba(255,255,255,0.4)',
                                backgroundColor: alpha('#fff', 0.15),
                                color: '#fff',
                                fontWeight: 800,
                                fontSize: '1rem',
                                flexShrink: 0,
                            }}
                        >
                            {displayInitials}
                        </Avatar>
                    )}
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                lineHeight: 1.2,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {displayName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {(collegeBio?.positions?.filter(isBaseballPosition)?.length
                                ? collegeBio.positions.filter(isBaseballPosition)
                                : bio?.positions?.filter(isBaseballPosition) || []
                            ).map((pos, i) => (
                                <Chip
                                    key={i}
                                    label={pos}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        backgroundColor: alpha('#fff', 0.25),
                                        color: '#fff',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    {(collegeBio?.jersey || bio?.jersey) && (
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 900, lineHeight: 1, opacity: 0.6 }}
                        >
                            #{collegeBio?.jersey || bio?.jersey}
                        </Typography>
                    )}
                    {collegeBio?.class_year ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ opacity: 0.9, fontWeight: 600, lineHeight: 1.3 }}
                            >
                                College: {collegeBio.class_year}
                            </Typography>
                            {bio?.class_year && (
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.7, lineHeight: 1.3 }}
                                >
                                    HS Class of &apos;{String(bio.class_year).slice(-2)}
                                </Typography>
                            )}
                        </Box>
                    ) : bio?.class_year ? (
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            HS Class of &apos;{String(bio.class_year).slice(-2)}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            {/* School info */}
            {bio?.school && (
                <Box
                    sx={{
                        px: 2,
                        py: 0.75,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <SportsBaseball sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                        {bio.school}
                        {bio.city && ` · ${bio.city}`}
                        {bio.state && `, ${bio.state}`}
                        {bio.height && ` · ${bio.height}`}
                        {bio.weight && ` / ${bio.weight} lbs`}
                    </Typography>
                </Box>
            )}

            {/* Highlight stats */}
            <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                {hasMultipleLevels ? (
                    <>
                        {/* College highlights */}
                        {showCollege && (collegeBatting || collegePitching) && (
                            <Box
                                sx={{
                                    borderLeft: `4px solid #8e24aa`,
                                    backgroundColor: alpha('#8e24aa', 0.04),
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#8e24aa',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontSize: '0.6rem',
                                        px: 1.5,
                                        pt: 0.5,
                                        display: 'block',
                                    }}
                                >
                                    College {collegeSchoolName ? `· ${collegeSchoolName}` : ''}
                                </Typography>
                                {collegeBatting && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                        }}
                                    >
                                        <StatBox label="AVG" value={collegeBatting.AVG} />
                                        <StatBox label="H" value={collegeBatting.H} />
                                        <StatBox label="HR" value={collegeBatting.HR} />
                                        <StatBox label="RBI" value={collegeBatting.RBI} />
                                        <StatBox label="OPS" value={collegeBatting.OPS} />
                                    </Box>
                                )}
                                {collegePitching && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                            ...(collegeBatting
                                                ? {
                                                      borderTop: `1px dashed ${alpha(
                                                          '#8e24aa',
                                                          0.15,
                                                      )}`,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        <StatBox label="ERA" value={collegePitching.ERA} />
                                        <StatBox
                                            label="W-L"
                                            value={`${collegePitching.W || '0'}-${
                                                collegePitching.L || '0'
                                            }`}
                                        />
                                        <StatBox label="K" value={collegePitching.K} />
                                        <StatBox label="BB" value={collegePitching.BB} />
                                        <StatBox label="WHIP" value={collegePitching.WHIP} />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {/* Summer League highlights */}
                        {showSummer && (summerBatting || summerPitching) && (
                            <Box
                                sx={{
                                    borderLeft: `4px solid #00897b`,
                                    backgroundColor: alpha('#00897b', 0.04),
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#00897b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontSize: '0.6rem',
                                        px: 1.5,
                                        pt: 0.5,
                                        display: 'block',
                                    }}
                                >
                                    Summer League
                                </Typography>
                                {summerBatting && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                        }}
                                    >
                                        <StatBox label="AVG" value={summerBatting.AVG} />
                                        <StatBox label="H" value={summerBatting.H} />
                                        <StatBox label="HR" value={summerBatting.HR} />
                                        <StatBox label="RBI" value={summerBatting.RBI} />
                                        <StatBox label="OPS" value={summerBatting.OPS} />
                                    </Box>
                                )}
                                {summerPitching && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                            ...(summerBatting
                                                ? {
                                                      borderTop: `1px dashed ${alpha(
                                                          '#00897b',
                                                          0.15,
                                                      )}`,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        <StatBox label="ERA" value={summerPitching.ERA} />
                                        <StatBox
                                            label="W-L"
                                            value={`${summerPitching.W || '0'}-${
                                                summerPitching.L || '0'
                                            }`}
                                        />
                                        <StatBox label="K" value={summerPitching.K} />
                                        <StatBox label="BB" value={summerPitching.BB} />
                                        <StatBox label="WHIP" value={summerPitching.WHIP} />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {/* HS highlights */}
                        {showHs && (hsBatting || hsPitching) && (
                            <Box
                                sx={{
                                    borderLeft: `4px solid #1565c0`,
                                    backgroundColor: alpha('#1565c0', 0.04),
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#1565c0',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontSize: '0.6rem',
                                        px: 1.5,
                                        pt: 0.5,
                                        display: 'block',
                                    }}
                                >
                                    High School
                                    {bio?.school ? ` · ${bio.school}` : ''}
                                </Typography>
                                {hsBatting && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                        }}
                                    >
                                        <StatBox label="AVG" value={hsBatting.AVG} />
                                        <StatBox label="H" value={hsBatting.H} />
                                        <StatBox label="HR" value={hsBatting.HR} />
                                        <StatBox label="RBI" value={hsBatting.RBI} />
                                        <StatBox label="OPS" value={hsBatting.OPS} />
                                    </Box>
                                )}
                                {hsPitching && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 0.75,
                                            px: 1,
                                            ...(hsBatting
                                                ? {
                                                      borderTop: `1px dashed ${alpha(
                                                          '#1565c0',
                                                          0.15,
                                                      )}`,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        <StatBox label="ERA" value={hsPitching.ERA} />
                                        <StatBox
                                            label="W-L"
                                            value={`${hsPitching.W || '0'}-${hsPitching.L || '0'}`}
                                        />
                                        <StatBox label="K" value={hsPitching.K} />
                                        <StatBox label="BB" value={hsPitching.BB} />
                                        <StatBox label="WHIP" value={hsPitching.WHIP} />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                ) : (
                    (() => {
                        const bat =
                            (showCollege && collegeBatting) ||
                            (showSummer && summerBatting) ||
                            (showHs && hsBatting) ||
                            null;
                        const pit =
                            (showCollege && collegePitching) ||
                            (showSummer && summerPitching) ||
                            (showHs && hsPitching) ||
                            null;
                        return (
                            <>
                                {bat && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 1,
                                            px: 1,
                                        }}
                                    >
                                        <StatBox label="AVG" value={bat.AVG} />
                                        <StatBox label="H" value={bat.H} />
                                        <StatBox label="HR" value={bat.HR} />
                                        <StatBox label="RBI" value={bat.RBI} />
                                        <StatBox label="OPS" value={bat.OPS} />
                                    </Box>
                                )}
                                {pit && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            py: 1,
                                            px: 1,
                                            ...(bat
                                                ? {
                                                      borderTop: `1px dashed ${alpha(
                                                          theme.palette.divider,
                                                          0.15,
                                                      )}`,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        <StatBox label="ERA" value={pit.ERA} />
                                        <StatBox
                                            label="W-L"
                                            value={`${pit.W || '0'}-${pit.L || '0'}`}
                                        />
                                        <StatBox label="K" value={pit.K} />
                                        <StatBox label="BB" value={pit.BB} />
                                        <StatBox label="WHIP" value={pit.WHIP} />
                                    </Box>
                                )}
                            </>
                        );
                    })()
                )}
            </Box>

            {/* College Stats Section */}
            {showCollege && hasCollegeData && (
                <>
                    <SectionDivider label="College Stats" color="#8e24aa" />
                    <BattingTable
                        seasons={collegeSeasons}
                        careerTotals={collegeCareerTotals?.batting}
                        theme={theme}
                        schoolName={collegeSchoolAbbr}
                        playerClassYear={collegePlayerClassYear}
                        gradYear={bio?.class_year}
                    />
                    <PitchingTable
                        seasons={collegeSeasons}
                        careerTotals={collegeCareerTotals?.pitching}
                        theme={theme}
                        schoolName={collegeSchoolAbbr}
                        playerClassYear={collegePlayerClassYear}
                        gradYear={bio?.class_year}
                    />
                </>
            )}

            {/* Summer League Stats Section */}
            {showSummer && hasSummerData && (
                <>
                    <SectionDivider label="Summer League Stats" color="#00897b" />
                    <BattingTable
                        seasons={summerSeasons}
                        careerTotals={summerCareerTotals?.batting}
                        theme={theme}
                        variant="summer"
                    />
                    <PitchingTable
                        seasons={summerSeasons}
                        careerTotals={summerCareerTotals?.pitching}
                        theme={theme}
                        variant="summer"
                    />
                </>
            )}

            {/* High School Stats Section */}
            {showHs && hasHsData && (
                <>
                    {(hasCollegeData || hasSummerData) && (
                        <SectionDivider label="High School Stats" color="#1565c0" />
                    )}
                    <BattingTable
                        seasons={hsSeasons}
                        careerTotals={hsCareerTotals?.batting}
                        theme={theme}
                    />
                    <PitchingTable
                        seasons={hsSeasons}
                        careerTotals={hsCareerTotals?.pitching}
                        theme={theme}
                    />
                </>
            )}

            {/* Fielding — expandable (from visible sources) */}
            {((showHs && (hsSeasons.some((s) => s.fielding) || hsCareerTotals?.fielding)) ||
                (showCollege &&
                    (collegeSeasons.some((s) => s.fielding) || collegeCareerTotals?.fielding)) ||
                (showSummer &&
                    (summerSeasons.some((s) => s.fielding) || summerCareerTotals?.fielding))) && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.25,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.action.hover, 0.04),
                            },
                        }}
                        onClick={() => setExpanded(!expanded)}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>
                            Fielding
                        </Typography>
                        <IconButton size="small">
                            <ExpandMoreIcon
                                sx={{
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                    fontSize: 16,
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Collapse in={expanded}>
                        <Box sx={{ px: 1, pb: 1 }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={META_CELL_HEADER_SX}>GD</TableCell>
                                            <TableCell sx={META_CELL_HEADER_SX}>Team</TableCell>
                                            <TableCell sx={META_CELL_HEADER_SX}>Year</TableCell>
                                            {FIELDING_COLUMNS.map((col) => (
                                                <TableCell key={col.key} sx={STAT_CELL_HEADER_SX}>
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[
                                            ...(showCollege ? collegeSeasons : []),
                                            ...(showSummer ? summerSeasons : []),
                                            ...(showHs ? hsSeasons : []),
                                        ]
                                            .filter((s) => s.fielding)
                                            .map((s, i) => {
                                                const isCollege = s.team_level === 'College';
                                                const isSummerRow = s.team_level === 'Summer';
                                                return (
                                                    <TableRow key={i}>
                                                        <TableCell sx={META_CELL_SX}>
                                                            {isSummerRow
                                                                ? s.age || '-'
                                                                : s.grade ||
                                                                  (isCollege
                                                                      ? collegePlayerClassYear
                                                                      : '') ||
                                                                  '-'}
                                                        </TableCell>
                                                        <TableCell sx={META_CELL_SX}>
                                                            {abbreviateSchool(
                                                                s.school_name ||
                                                                    (isCollege
                                                                        ? collegeSchoolName
                                                                        : '') ||
                                                                    (s.team_level || '').slice(
                                                                        0,
                                                                        3,
                                                                    ),
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={META_CELL_SX}>
                                                            {isSummerRow
                                                                ? s.league_name ||
                                                                  s.league_abbr ||
                                                                  '-'
                                                                : s.year}
                                                        </TableCell>
                                                        {FIELDING_COLUMNS.map((col) => (
                                                            <TableCell
                                                                key={col.key}
                                                                sx={STAT_CELL_SX}
                                                            >
                                                                {s.fielding?.[col.key] || '-'}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Collapse>
                </>
            )}
        </Box>
    );
};

export default BaseballCard;
